import { useState, useCallback, useContext, useMemo, useEffect } from "react";
import { SettingsSection } from "components/Settings/SettingsSection";
import { useFetchPatchV2 } from "hooks/fetch/useFetchPatchV2";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { FeaturesContext } from "contexts/FeaturesContext";
import { CustomPrompt } from "components/CustomPrompt";
import isEqual from "lodash/isEqual";
import { styled } from "@mui/material/styles";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Button
} from "@mui/material";

import Paper from "@mui/material/Paper";

export const EnabledFeaturesSection = () => {
  const { loading: featuresLoading, features, refetchFeatures } = useContext(
    FeaturesContext
  );

  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);

  const [checked, setChecked] = useState(null);

  const {
    patchCallback: patchEnabledFeature,
    loading: patchEnabledFeatureLoading
  } = useFetchPatchV2(`settings/features`);

  useEffect(() => {
    setChecked(features);
  }, [features]);

  const editMode = useMemo(() => {
    if (!checked || !features) {
      return false;
    }
    return !isEqual(features, checked);
  }, [features, checked]);

  const handlePatchEnabledFeature = useCallback(() => {
    patchEnabledFeature(checked).then(
      () => {
        refetchFeatures().catch(() => {
          addSnackbarToQueue({
            message: "Failed to refresh features.",
            severity: "error"
          });
        });
        addSnackbarToQueue({
          message: "Enabled Features updated.",
          severity: "success"
        });
      },
      () => {
        addSnackbarToQueue({
          message: "Enabled Features failed to save.",
          severity: "error"
        });
      }
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked]);

  const switchHandler = e => {
    setChecked(prevState => ({
      ...prevState,
      [e.target.name]: e.target.checked
    }));
  };

  return (
    <>
      <CustomPrompt
        shouldBlockNavigation={editMode}
        content={
          "You have unsaved changes in Enabled Features. Are you sure you want to navigate away?"
        }
      />
      {/* inputProps={{ "aria-label": "prompt" }} */}

      <SettingsSection title="Enabled Features">
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell data-col={1}>Name</TableCell>
                <TableCell data-col={2}>Enabled</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell data-col={1}>Case Management</TableCell>
                <TableCell data-col={2}>
                  <Switch
                    name="case_management"
                    inputProps={{
                      "aria-label": "toggle case management feature"
                    }}
                    data-testid="casemanagement-toggle"
                    checked={checked?.case_management ?? false}
                    onChange={switchHandler}
                    disabled={featuresLoading}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell data-col={1}>Alerts</TableCell>
                <TableCell data-col={2}>
                  <Switch
                    name="alerts"
                    inputProps={{
                      "aria-label": "toggle alerts feature"
                    }}
                    data-testid="alerts-toggle"
                    checked={checked?.alerts ?? false}
                    onChange={switchHandler}
                    disabled={featuresLoading}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell data-col={1}>Overviews</TableCell>
                <TableCell data-col={2}>
                  <Switch
                    name="overviews"
                    inputProps={{
                      "aria-label": "toggle overviews feature"
                    }}
                    data-testid="overviews-toggle"
                    checked={checked?.overviews ?? false}
                    onChange={switchHandler}
                    disabled={featuresLoading}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell data-col={1}>Fleet Manager</TableCell>
                <TableCell data-col={2}>
                  <Switch
                    name="powerbi"
                    inputProps={{ "aria-label": "toggle powerbi feature" }}
                    data-testid="powerbi-toggle"
                    checked={checked?.powerbi ?? false}
                    onChange={switchHandler}
                    disabled={featuresLoading}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell data-col={1}>
                  Down/Derated Tracker (requires Case Management)
                </TableCell>
                <TableCell data-col={2}>
                  <Switch
                    name="down_derated_tracker"
                    inputProps={{
                      "aria-label": "toggle down derated tracker feature"
                    }}
                    checked={checked?.down_derated_tracker ?? false}
                    onChange={switchHandler}
                    disabled={featuresLoading}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell data-col={1}>Data Exploration</TableCell>
                <TableCell data-col={2}>
                  <Switch
                    name="data_exploration_v2"
                    inputProps={{
                      "aria-label": "toggle data exploration feature beta"
                    }}
                    checked={checked?.data_exploration_v2 ?? false}
                    onChange={switchHandler}
                    disabled={featuresLoading}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell data-col={1}>Lost Energy</TableCell>
                <TableCell data-col={2}>
                  <Switch
                    name="lost_energy_dashboard"
                    inputProps={{
                      "aria-label": "toggle lost energy feature"
                    }}
                    checked={checked?.lost_energy_dashboard ?? false}
                    onChange={switchHandler}
                    disabled={featuresLoading}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell data-col={1}>
                  Lost Energy - Predicted Losses
                </TableCell>
                <TableCell data-col={2}>
                  <Switch
                    name="lost_energy_predicted_losses"
                    inputProps={{
                      "aria-label":
                        "toggle lost energy predicted losses feature"
                    }}
                    checked={checked?.lost_energy_predicted_losses ?? false}
                    onChange={switchHandler}
                    disabled={featuresLoading}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell data-col={1}>
                  My Actions (requires Case Management)
                </TableCell>
                <TableCell data-col={2}>
                  <Switch
                    name="my_actions"
                    inputProps={{
                      "aria-label": "toggle my actions feature"
                    }}
                    checked={checked?.my_actions ?? false}
                    onChange={switchHandler}
                    disabled={featuresLoading}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell data-col={1}>Work Orders</TableCell>
                <TableCell data-col={2}>
                  <Switch
                    name="work_orders"
                    inputProps={{
                      "aria-label": "toggle work orders feature"
                    }}
                    checked={checked?.work_orders ?? false}
                    onChange={switchHandler}
                    disabled={featuresLoading}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell data-col={1}>
                  External Actions for Work Orders
                </TableCell>
                <TableCell data-col={2}>
                  <Switch
                    name="external_actions_for_work_orders"
                    inputProps={{
                      "aria-label":
                        "toggle external actions for work orders feature"
                    }}
                    data-testid="externalactionsforworkorders-toggle"
                    checked={checked?.external_actions_for_work_orders ?? false}
                    onChange={switchHandler}
                    disabled={featuresLoading}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell data-col={1}>
                  Projects (requires Case Management)
                </TableCell>
                <TableCell data-col={2}>
                  <Switch
                    name="projects"
                    inputProps={{
                      "aria-label": "toggle projects feature"
                    }}
                    checked={checked?.projects ?? false}
                    onChange={switchHandler}
                    disabled={featuresLoading}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell data-col={1}>
                  Case Table Grouping (requires Case Management)
                </TableCell>
                <TableCell data-col={2}>
                  <Switch
                    name="case_table_groupby"
                    inputProps={{
                      "aria-label": "toggle case table groupby feature"
                    }}
                    checked={checked?.case_table_groupby ?? false}
                    onChange={switchHandler}
                    disabled={featuresLoading}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell data-col={1}>
                  Key Performance Indicators (KPIs)
                </TableCell>
                <TableCell data-col={2}>
                  <Switch
                    name="kpis"
                    inputProps={{
                      "aria-label": "toggle KPIs feature"
                    }}
                    checked={checked?.kpis ?? false}
                    onChange={switchHandler}
                    disabled={featuresLoading}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <ButtonWrapper>
          <Button
            onClick={handlePatchEnabledFeature}
            color="primary"
            variant="contained"
            disabled={
              !editMode || patchEnabledFeatureLoading || featuresLoading
            }
            data-qa="Settings-Button-Save-Enabled-Features"
          >
            Save
          </Button>
        </ButtonWrapper>
      </SettingsSection>
    </>
  );
};

const ButtonWrapper = styled("div")`
  display: flex;
  justify-content: flex-end;
  padding: ${({ theme }) => theme.spacing(1)};
`;
